import React, { useState } from 'react';
import { useArticles } from '../../api/ArticleContext2';
import { Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Tag,
  Statistic,
  List,
  Typography,
  Space,
  Empty,
  Affix,
  Grid,
  Divider,
  Pagination
} from 'antd';
import {
  CalendarOutlined,
  FolderOutlined,
  FileTextOutlined,
  ReadOutlined,
  UserOutlined,
  ClearOutlined,
  UnorderedListOutlined
} from '@ant-design/icons';
import './Archive.css';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const Archive = () => {
  const { getArticlesByYear, getAllArticles } = useArticles();
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const screens = useBreakpoint();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  
  const articlesByYear = getArticlesByYear();
  const allArticles = getAllArticles();
  
  const years = Object.keys(articlesByYear).sort((a, b) => b - a);

  // 统计分类
  const categories = {};
  allArticles.forEach(article => {
    const category = article.category || '未分类';
    if (!categories[category]) {
      categories[category] = 0;
    }
    categories[category]++;
  });

  // 处理分类点击
  const handleCategoryClick = (categoryName) => {
    setSelectedCategory(categoryName);
    setCurrentPage(1);
  };

  // 处理年份点击
  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedCategory('all');
    setCurrentPage(1);
  };

  // 获取当前显示的文章
  const getCurrentArticles = () => {
    let filteredArticles = [];
    
    if (selectedYear !== 'all' && articlesByYear[selectedYear]) {
      filteredArticles = [...articlesByYear[selectedYear]];
    } else {
      Object.values(articlesByYear).forEach(yearArticles => {
        filteredArticles.push(...yearArticles);
      });
    }
    
    if (selectedCategory !== 'all') {
      filteredArticles = filteredArticles.filter(
        article => article.category === selectedCategory
      );
    }
    
    return filteredArticles;
  };

  const currentArticles = getCurrentArticles();
  
  // 获取分页文章
  const getPaginatedArticles = () => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return currentArticles.slice(start, end);
  };

  const paginatedArticles = getPaginatedArticles();
  const totalPages = Math.ceil(currentArticles.length / pageSize);
  
  // 为分类列表添加"全部"选项
  const categoryList = [
    { name: 'all', displayName: '全部', count: allArticles.length },
    ...Object.entries(categories).map(([name, count]) => ({ 
      name, 
      displayName: name,
      count 
    }))
  ];

  // 为年份列表添加"全部"选项
  const yearList = [
    { year: 'all', displayName: '全部', count: allArticles.length },
    ...years.map(year => ({ 
      year, 
      displayName: `${year}年`,
      count: articlesByYear[year].length
    }))
  ];

  // 侧边栏内容
  const sidebarContent = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* 年份筛选 */}
      <Card 
        size="small" 
        title="按年份"
        bordered={false}
        className="filter-card"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {yearList.map(item => (
            <Button
              key={item.year}
              type={selectedYear === item.year ? 'primary' : 'text'}
              block
              onClick={() => handleYearClick(item.year)}
              className="filter-btn"
              style={{ 
                textAlign: 'left',
                padding: '8px 12px',
                borderRadius: '4px'
              }}
            >
              <span style={{ flex: 1 }}>{item.displayName}</span>
              <span style={{ color: selectedYear === item.year ? '#fff' : '#999' }}>
                {item.count}
              </span>
            </Button>
          ))}
        </Space>
      </Card>

      <Divider style={{ margin: '12px 0' }} />

      {/* 分类筛选 */}
      <Card 
        size="small" 
        title="分类"
        bordered={false}
        className="filter-card"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {categoryList.map(category => (
            <Button
              key={category.name}
              type={selectedCategory === category.name ? 'primary' : 'text'}
              block
              onClick={() => handleCategoryClick(category.name)}
              className="filter-btn"
              style={{ 
                textAlign: 'left',
                padding: '8px 12px',
                borderRadius: '4px'
              }}
            >
              <span style={{ flex: 1 }}>{category.displayName}</span>
              <span style={{ color: selectedCategory === category.name ? '#fff' : '#999' }}>
                {category.count}
              </span>
            </Button>
          ))}
        </Space>
      </Card>

      <Divider style={{ margin: '12px 0' }} />

      {/* 统计信息 */}
      <Card 
        size="small" 
        title="统计"
        bordered={false}
        className="filter-card"
      >
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '500', color: '#1890ff' }}>
                {allArticles.length}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                文章总数
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '500', color: '#52c41a' }}>
                {years.length}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                写作年份
              </div>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '18px', fontWeight: '500', color: '#722ed1' }}>
                {Object.keys(categories).length}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                分类数量
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </Space>
  );

  return (
    <div className="archive">
      <div className="archive-header">
        <Title level={2} style={{ marginBottom: 8, fontWeight: 500 }}>
          <UnorderedListOutlined style={{ marginRight: 12, color: '#1890ff' }} />
          文章归档
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          记录每一段代码的旅程，汇聚每一份技术的思考
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {/* 侧边栏 */}
        <Col xs={24} md={8} lg={6}>
          {screens.md ? (
            <Affix offsetTop={80}>
              {sidebarContent}
            </Affix>
          ) : (
            sidebarContent
          )}
        </Col>

        {/* 主内容区 */}
        <Col xs={24} md={16} lg={18}>
          <Card 
            bordered={false}
            bodyStyle={{ padding: '24px' }}
            className="content-card"
          >
            {/* 标题和筛选标签 */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={4} style={{ margin: 0, fontWeight: 500 }}>
                  {selectedYear !== 'all' ? `${selectedYear}年` : '所有年份'}
                  {selectedCategory !== 'all' ? ` - ${selectedCategory}分类` : ''}
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 14, fontWeight: 400 }}>
                    （{currentArticles.length} 篇）
                  </Text>
                </Title>
                
                {(selectedYear !== 'all' || selectedCategory !== 'all') && (
                  <Button
                    type="text"
                    size="small"
                    icon={<ClearOutlined />}
                    onClick={() => {
                      setSelectedYear('all');
                      setSelectedCategory('all');
                      setCurrentPage(1);
                    }}
                  >
                    清除
                  </Button>
                )}
              </div>

              {(selectedYear !== 'all' || selectedCategory !== 'all') && (
                <Space wrap style={{ marginBottom: 8 }}>
                  {selectedYear !== 'all' && (
                    <Tag
                      closable
                      onClose={() => handleYearClick('all')}
                      style={{ borderRadius: '12px', padding: '2px 8px', fontSize: '12px' }}
                    >
                      {selectedYear}年
                    </Tag>
                  )}
                  {selectedCategory !== 'all' && (
                    <Tag
                      closable
                      onClose={() => handleCategoryClick('all')}
                      style={{ borderRadius: '12px', padding: '2px 8px', fontSize: '12px' }}
                    >
                      {selectedCategory}
                    </Tag>
                  )}
                </Space>
              )}
            </div>

            {/* 文章列表 */}
            {currentArticles.length > 0 ? (
              <>
                <List
                  itemLayout="vertical"
                  size="large"
                  dataSource={paginatedArticles}
                  className="articles-list"
                  renderItem={(article) => (
                    <List.Item
                      key={article.id}
                      style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}
                    >
                      <List.Item.Meta
                        title={
                          <Link to={`/article/${article.id}`} style={{ color: '#1890ff' }}>
                            <Title level={5} style={{ marginBottom: 8, fontWeight: 500 }}>
                              {article.title}
                            </Title>
                          </Link>
                        }
                        description={
                          <Space size="middle" wrap style={{ marginBottom: 8 }}>
                            <Space size="small">
                              <CalendarOutlined style={{ fontSize: '12px', color: '#999' }} />
                              <Text type="secondary" style={{ fontSize: '12px' }}>{article.date}</Text>
                            </Space>
                            <Space size="small">
                              <FolderOutlined style={{ fontSize: '12px', color: '#999' }} />
                              <Text type="secondary" style={{ fontSize: '12px' }}>{article.category}</Text>
                            </Space>
                            <Space size="small">
                              <UserOutlined style={{ fontSize: '12px', color: '#999' }} />
                              <Text type="secondary" style={{ fontSize: '12px' }}>{article.author}</Text>
                            </Space>
                          </Space>
                        }
                      />
                      <Paragraph 
                        type="secondary" 
                        style={{ 
                          fontSize: '14px',
                          lineHeight: '1.6',
                          marginBottom: '12px'
                        }}
                      >
                        {article.excerpt}
                      </Paragraph>
                      <div style={{ textAlign: 'right' }}>
                        <Link 
                          to={`/article/${article.id}`} 
                          style={{ 
                            fontSize: '14px',
                            color: '#1890ff',
                            textDecoration: 'none'
                          }}
                        >
                          阅读全文 →
                        </Link>
                      </div>
                    </List.Item>
                  )}
                />
                
                {totalPages > 1 && (
                  <div style={{ marginTop: 24, textAlign: 'center' }}>
                    <Pagination
                      current={currentPage}
                      total={currentArticles.length}
                      pageSize={pageSize}
                      onChange={(page) => setCurrentPage(page)}
                      showSizeChanger={false}
                      showQuickJumper
                      showTotal={(total) => `共 ${total} 条`}
                    />
                  </div>
                )}
              </>
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                               description={
                  <div>
                    <div style={{ marginBottom: 8 }}>暂无文章</div>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                      尝试选择其他年份或分类查看文章
                    </Text>
                  </div>
                }
              >
                <Button
                  type="primary"
                  onClick={() => {
                    setSelectedYear('all');
                    setSelectedCategory('all');
                  }}
                >
                  重置筛选
                </Button>
              </Empty>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Archive;